import { useState, useEffect, useMemo, useRef } from "react";
import { Card, Row, Col, Typography, Avatar, Statistic, Space, Spin, DatePicker } from "antd";
import GettingStartedWidget from "../admin-dashboard/GettingStartedWidget";
import DashboardStats from "./DashboardStats";
import {
  IoCall,
  IoCallOutline,
  IoTrendingUp,
  IoTrendingDown,
} from "react-icons/io5";
import { FaPhoneSlash } from "react-icons/fa";
import ReactApexChart from "react-apexcharts";
import { useSelector, useDispatch } from "react-redux";
import "./Dashboard.css";
import api from "../../../core/axios/axiosInstance";


import dayjs from "dayjs";
import {
  fetchCallGraph,
  fetchDashboardCallHistory,
  setGraphLoading,
  setHistoryLoading,
} from "../../../core/data/redux/slices/DashboardDataSlice";

const { Title, Text } = Typography;

const Dashboard = () => {
  const dispatch = useDispatch();
  // const now = new Date();
  // const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  // const yesterday = new Date(today);
  // yesterday.setDate(today.getDate() - 1);

  // const last7Start = new Date(today);
  // last7Start.setDate(today.getDate() - 7);

  // const last30Start = new Date(today);
  // last30Start.setDate(today.getDate() - 30);

  // const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  // const thisMonthEnd = new Date(
  //   now.getFullYear(),
  //   now.getMonth() + 1,
  //   0,
  //   23,
  //   59,
  //   59,
  //   999
  // );

  // const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  // const lastMonthEnd = new Date(
  //   now.getFullYear(),
  //   now.getMonth(),
  //   0,
  //   23,
  //   59,
  //   59,
  //   999
  // );
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  const last7Start = new Date(today);
  last7Start.setDate(today.getDate() - 7);

  const last30Start = new Date(today);
  last30Start.setDate(today.getDate() - 30);

  const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const thisMonthEnd = new Date(
    now.getFullYear(),
    now.getMonth() + 1,
    0,
    23,
    59,
    59,
    999
  );

  const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const lastMonthEnd = new Date(
    now.getFullYear(),
    now.getMonth(),
    0,
    23,
    59,
    59,
    999
  );
  const profile = useSelector((state) => state.profile);
  const leadStatuses = profile?.leadStatuses ?? [];

  const { callHistory, callGraph, loadingHistory, loadingGraph, errorGraph, errorHistory } = useSelector(
    (state) => state.dashboardData
  );

  // Lead status pyramid state — fetched once, not tied to date picker.
  // Start loading=true so the spinner shows immediately while we wait for profile.
  const [leadStatusCounts, setLeadStatusCounts] = useState([]);
  const [loadingLeadCounts, setLoadingLeadCounts] = useState(true);
  const [staticTotalLeads, setStaticTotalLeads] = useState(0);



  const [startDate, setStartDate] = useState(
    dayjs(last7Start).format("YYYY-MM-DD")
  );
  const [endDate, setEndDate] = useState(dayjs(today).format("YYYY-MM-DD"));

  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return "";
    const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    const [y, m, d] = dateStr.split("-").map(Number);
    return `${String(d).padStart(2, "0")} ${months[m - 1]} ${y}`;
  };
  const [activeTab, setActiveTab] = useState("7d");
  const [showCustomPicker, setShowCustomPicker] = useState(false);

  const DATE_TABS = [
    { id: "today",      label: "Today",      start: today,          end: today },
    { id: "yesterday",  label: "Yesterday",  start: yesterday,      end: yesterday },
    { id: "7d",         label: "7 Days",     start: last7Start,     end: today },
    { id: "30d",        label: "30 Days",    start: last30Start,    end: today },
    { id: "this_month", label: "This Month", start: thisMonthStart, end: today },
    { id: "last_month", label: "Last Month", start: lastMonthStart, end: lastMonthEnd },
  ];

  const applyTab = (tab) => {
    setActiveTab(tab.id);
    setShowCustomPicker(false);
    setStartDate(dayjs(tab.start).format("YYYY-MM-DD"));
    setEndDate(dayjs(tab.end).format("YYYY-MM-DD"));
  };

  const handleCustomRange = (dates) => {
    if (!dates || !dates[0] || !dates[1]) return;
    setStartDate(dates[0].format("YYYY-MM-DD"));
    setEndDate(dates[1].format("YYYY-MM-DD"));
  };

  // ── Chart configurations (all memoized so ApexCharts never gets a new object
  //    reference on unrelated re-renders, which causes blank / double-paint bugs
  //    in production builds) ───────────────────────────────────────────────────
  // callDays must be memoized — a plain `|| []` creates a new array reference
  // every render when callGraph is null, which would re-run every useMemo below.
  const callDays = useMemo(() => callGraph?.days || [], [callGraph]);

  const lineChartOptions = useMemo(() => ({
    chart: {
      type: "line",
      height: 350,
      toolbar: { show: false },
      zoom: { enabled: false },
      animations: { enabled: false },
    },
    noData: {
      text: "No data for this period",
      align: "center",
      verticalAlign: "middle",
      style: { fontSize: "14px", color: "#aaa" },
    },
    colors: ["#52c41a", "#1890ff"],
    dataLabels: { enabled: false },
    stroke: { curve: "smooth", width: 3 },
    grid: { row: { colors: ["#f3f3f3", "transparent"], opacity: 0.5 } },
    xaxis: {
      categories: callDays.map((item) => `${item.date}`),
      title: { text: "Date" },
      labels: {
        rotate: -45,
        rotateAlways: false,
        hideOverlappingLabels: true,
        style: { fontSize: "10px" },
      },
    },
    yaxis: {
      title: { text: "Number of Calls", style: { fontSize: "12px" } },
      labels: { style: { fontSize: "10px" } },
    },
    legend: { position: "top", horizontalAlign: "right", fontSize: "12px" },
    responsive: [
      {
        breakpoint: 768,
        options: {
          chart: { height: 300 },
          xaxis: {
            labels: { rotate: -45, rotateAlways: true, maxHeight: 80, style: { fontSize: "9px" } },
            title: { text: "Date", style: { fontSize: "10px" } },
            tickAmount: 10,
          },
          yaxis: { title: { text: "Calls", style: { fontSize: "10px" } }, labels: { style: { fontSize: "9px" } } },
          legend: { position: "bottom", horizontalAlign: "center", fontSize: "10px" },
        },
      },
      {
        breakpoint: 480,
        options: {
          chart: { height: 250 },
          xaxis: { labels: { rotate: -45, rotateAlways: true, maxHeight: 70, style: { fontSize: "8px" } }, tickAmount: 8 },
          legend: { fontSize: "9px" },
        },
      },
    ],
  }), [callDays]);


































































  // Fetch lead counts per status group for pyramid chart — runs once on mount, not date-dependent.
  // We use profile.isLoading to gate the fetch: only act once the full profile
  // (including leadStatuses) has been loaded.  Using profile.id alone is unreliable
  // because the login action can set profile.id before fetchProfile completes, which
  // would fire this effect with an empty leadStatuses and permanently show the empty state.
  useEffect(() => {
    // Still loading — keep spinner, don't act yet
    if (profile?.isLoading !== false) return;

    if (!leadStatuses || leadStatuses.length === 0) {
      // Profile is fully loaded but has no lead statuses configured
      setLoadingLeadCounts(false);
      return;
    }

    const fetchLeadCounts = async () => {
      setLoadingLeadCounts(true);
      try {
        // Group statuses by group number
        const grouped = {};
        leadStatuses.forEach((s) => {
          if (!grouped[s.group]) grouped[s.group] = [];
          grouped[s.group].push(s);
        });
        const groupEntries = Object.entries(grouped);

        // Fetch all-time total leads (no date filter) alongside status counts
        const [totalRes, ...groupResults] = await Promise.all([
          api.post("/getAllContactsOrLeads", { isLead: true, page: 1, limit: 1, category: "lead" }),
          ...groupEntries.map(async ([groupNum, statuses]) => {
            const statusValues = statuses.map((s) => s.value);
            const res = await api.post("/getAllContactsOrLeads", {
              isLead: true,
              page: 1,
              limit: 1,
              status: statusValues,
              category: "lead",
            });
            const isWinLost = statuses.some(
              (s) =>
                s.value === "winLost" ||
                s.label.toLowerCase().includes("win") ||
                s.label.toLowerCase().includes("lost")
            );
            return {
              group: parseInt(groupNum),
              label: statuses.map((s) => s.label).join(" / "),
              count: res.data?.pagination?.totalItems ?? res.data?.pagination?.total ?? 0,
              isWinLost,
            };
          }),
        ]);

        setStaticTotalLeads(totalRes.data?.pagination?.totalItems ?? totalRes.data?.pagination?.total ?? 0);
        setLeadStatusCounts(groupResults);
      } catch (e) {
        console.error("Failed to fetch lead counts", e);
      } finally {
        setLoadingLeadCounts(false);
      }
    };
    fetchLeadCounts();
  }, [leadStatuses, profile?.isLoading]);

  useEffect(() => {
    dispatch(fetchDashboardCallHistory({ startDate, endDate }));
    dispatch(fetchCallGraph({ startDate, endDate }));
  }, [startDate, endDate]);

  const lineChartSeries = useMemo(() => [
    {
      name: "Inbound Calls",
      data: callDays.map((item) => item.inbound ?? 0),
    },
    {
      name: "Outbound Calls",
      data: callDays.map((item) => item.outbound ?? 0),
    },
  ], [callDays]);

  // ── Stat-card counts ───────────────────────────────────────────────────────
  // Inbound / Outbound / Total are derived from callGraph.days (same source as
  // the line chart) so they are ALWAYS consistent with the chart, even when the
  // separate dashboard-call-history API is slow or temporarily unavailable.
  const inboundTotal  = useMemo(() => callDays.reduce((s, d) => s + (d.inbound  ?? 0), 0), [callDays]);
  const outboundTotal = useMemo(() => callDays.reduce((s, d) => s + (d.outbound ?? 0), 0), [callDays]);
  const graphTotal    = inboundTotal + outboundTotal;

  // Missed / Answered / Cancelled / Invalid still come from callHistory because
  // the graph endpoint doesn't carry call-status breakdowns.
  const answered   = callHistory?.summary?.answeredTotal   ?? 0;
  const missed     = callHistory?.summary?.missedTotal     ?? 0;
  const cancelled  = callHistory?.summary?.cancelledTotal  ?? 0;
  const invalid    = callHistory?.summary?.invalidTotal    ?? 0;
  // Use graphTotal as the authoritative total so the radial chart denominator
  // is consistent with the stat cards.
  const totalCalls = graphTotal || (callHistory?.summary?.totalCalls ?? 0);

  // ── Auto-retry for Lambda cold-start ────────────────────────────────────────
  // On the very first Lambda invocation after a deploy/cold-start, the MongoDB
  // $dateFromString aggregation in getMonthlyCallGraph can return 0 for all
  // status breakdowns (answered/missed/cancelled/invalid) even when there ARE
  // calls — the connection pool hasn't warmed up yet.  We detect this case:
  // callHistory loaded (not null, not loading) + totalCalls > 0 + all statuses 0
  // and automatically retry once after a short delay so the user always sees
  // correct data without having to manually change the date filter.
  const retryFiredRef = useRef(false);
  useEffect(() => {
    const statusSum = answered + missed + cancelled + invalid;
    const staleResponse = !loadingHistory && callHistory !== null && totalCalls > 0 && statusSum === 0;

    if (staleResponse && !retryFiredRef.current && profile?.id) {
      retryFiredRef.current = true;
      const timer = setTimeout(() => {
        dispatch(fetchDashboardCallHistory({ startDate, endDate }));
      }, 1500); // wait 1.5s then retry
      return () => clearTimeout(timer);
    }

    // Reset retry flag when date changes (allow retry on next period too)
    if (!staleResponse) retryFiredRef.current = false;
  }, [loadingHistory, callHistory, totalCalls, answered, missed, cancelled, invalid, startDate, endDate, profile?.id]);

  // ── Retry on CORS/network failure (Lambda cold-start) ───────────────────────
  // On initial page load all dashboard requests fire simultaneously, which can
  // saturate Lambda cold-start slots and cause OPTIONS preflight requests to get
  // a 503, which the browser reports as a CORS failure.  We detect an error on
  // either endpoint and retry once after 2 s — by then Lambda is warm and the
  // retried request succeeds.  The ref gate prevents infinite retry loops.
  const graphRetryRef   = useRef(false);
  const historyRetryRef = useRef(false);

  useEffect(() => {
    if (!errorGraph) { graphRetryRef.current = false; return; }
    if (loadingGraph || graphRetryRef.current) return;
    graphRetryRef.current = true;
    // Keep skeleton visible during the retry window so 0s never flash
    dispatch(setGraphLoading());
    const t = setTimeout(() => dispatch(fetchCallGraph({ startDate, endDate })), 2000);
    return () => clearTimeout(t);
  }, [errorGraph, loadingGraph, startDate, endDate]);

  useEffect(() => {
    if (!errorHistory) { historyRetryRef.current = false; return; }
    if (loadingHistory || historyRetryRef.current) return;
    historyRetryRef.current = true;
    dispatch(setHistoryLoading());
    const t = setTimeout(() => dispatch(fetchDashboardCallHistory({ startDate, endDate })), 2000);
    return () => clearTimeout(t);
  }, [errorHistory, loadingHistory, startDate, endDate]);

  const hasData    = totalCalls > 0;

  // radialBar series must be percentages (0–100)
  const finalSeries = hasData
    ? [
        Math.round((answered / totalCalls) * 100),
        Math.round((missed / totalCalls) * 100),
        Math.round((cancelled / totalCalls) * 100),
        Math.round((invalid / totalCalls) * 100),
      ]
    : [0, 0, 0, 0];

  const pieChartOptions = useMemo(
    () => ({
      chart: {
        type: "radialBar",
        height: 350,
        toolbar: { show: false },
        animations: { enabled: false },
      },
      colors: ["#52c41a", "#ff4d4f", "#faad14", "#14a6fa"],
      labels: ["Answered", "Missed", "Cancelled", "Invalid"],
      plotOptions: {
        radialBar: {
          offsetY: 0,
          startAngle: 0,
          endAngle: 270,
          hollow: {
            margin: 5,
            size: "30%",
            background: "transparent",
          },
          dataLabels: {
            name: { show: false },
            value: { show: false },
            total: {
              show: true,
              label: hasData ? "Total" : "No Data",
              formatter: () => (hasData ? `${totalCalls}` : ""),
              fontSize: "16px",
              fontFamily: "inherit",
              fontWeight: 600,
            },
          },
          track: {
            background: "#f0f0f0",
          },
        },
      },
      legend: {
        show: true,
        floating: true,
        fontSize: "12px",
        position: "left",
        offsetX: 0,
        offsetY: 15,
        labels: { useSeriesColors: true },
        formatter: (seriesName, opts) =>
          `${seriesName}: ${opts.w.globals.series[opts.seriesIndex]}%`,
      },
      tooltip: {
        enabled: true,
        custom: ({ seriesIndex, w }) => {
          const callCounts = [answered, missed, cancelled, invalid];
          const callLabels = ["Answered", "Missed", "Cancelled", "Invalid"];
          const callColors = ["#52c41a", "#ff4d4f", "#faad14", "#14a6fa"];
          const count = callCounts[seriesIndex] ?? 0;
          const pct = w.globals.series[seriesIndex] ?? 0;
          return `<div style="padding:8px 14px;font-size:13px;font-family:inherit;line-height:1.6;">
            <span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${callColors[seriesIndex]};margin-right:6px;"></span>
            <strong>${callLabels[seriesIndex]}</strong>: ${count} calls (${pct}%)
          </div>`;
        },
      },
      responsive: [
        {
          breakpoint: 768,
          options: {
            chart: { height: 280 },
            legend: { fontSize: "11px" },
          },
        },
        {
          breakpoint: 480,
          options: {
            chart: { height: 240 },
            legend: { fontSize: "10px" },
          },
        },
      ],
    }),
    [answered, missed, cancelled, invalid, totalCalls, hasData]
  );

  // Pyramid chart data — uses staticTotalLeads (all-time, not date-filtered)
  const pyramidData = useMemo(() => {
    // Build from leadStatuses definitions even when every count is 0, so all
    // pipeline steps always render rather than showing an empty state.
    if (!leadStatusCounts.length && !leadStatuses.length) return { series: [], labels: [], actualCounts: [] };

    // If we have status definitions but the fetch hasn't returned yet, stub out
    // the groups with 0 counts so the pyramid frame shows immediately.
    const effectiveCounts = leadStatusCounts.length
      ? leadStatusCounts
      : (() => {
          const grouped = {};
          leadStatuses.forEach((s) => {
            if (!grouped[s.group]) grouped[s.group] = [];
            grouped[s.group].push(s);
          });
          return Object.entries(grouped).map(([groupNum, statuses]) => ({
            group:    parseInt(groupNum),
            label:    statuses.map((s) => s.label).join(" / "),
            count:    0,
            isWinLost: statuses.some((s) => s.value === "winLost" || s.label.toLowerCase().includes("win") || s.label.toLowerCase().includes("lost")),
          }));
        })();

    // Use all-time total (not date-filtered) so pyramid doesn't react to date picker changes
    const realTotal = staticTotalLeads || effectiveCounts.reduce((sum, g) => sum + g.count, 0);

    // Include ALL groups (even count = 0) so every status step is always visible
    const winLostGroups = effectiveCounts.filter((g) => g.isWinLost);
    const otherGroups = effectiveCounts
      .filter((g) => !g.isWinLost)
      .sort((a, b) => a.count - b.count); // ascending → narrowest at top

    // Pyramid order: Win/Lost (narrowest/top) → middles → Total Leads (widest/bottom)
    const items = [...winLostGroups, ...otherGroups];
    // Always show Total Leads row (even if 0)
    items.push({ label: "Total Leads", count: realTotal });

    // Use rank-based widths (1…N) so every bar is clearly visible regardless of actual count gaps.
    // Actual counts go into labels and tooltip.
    return {
      series: items.map((_, i) => i + 1),
      labels: items.map((item) =>
        item.label === "Total Leads"
          ? `Total Leads: ${item.count}`
          : `${item.label}: ${item.count}`
      ),
      actualCounts: items.map((i) => i.count),
    };
  }, [leadStatusCounts, leadStatuses, staticTotalLeads]);

  const funnelChartOptions = useMemo(
    () => ({
      chart: {
        type: "bar",
        height: Math.max(280, pyramidData.labels.length * 52),
        toolbar: { show: false },
      },
      plotOptions: {
        bar: {
          borderRadius: 0,
          horizontal: true,
          distributed: true,
          isFunnel: true,
        },
      },
      colors: ["#d9b8f5", "#be8ce0", "#9b84d9", "#7660c0", "#52419a", "#3C2371"],
      dataLabels: {
        enabled: true,
        formatter: (val, opt) => opt.w.globals.labels[opt.dataPointIndex],
        style: { fontSize: "12px", fontWeight: 600, colors: ["#fff"] },
        dropShadow: { enabled: true, blur: 3, opacity: 0.5 },
      },
      xaxis: {
        categories: pyramidData.labels,
        labels: { show: false },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: { show: false },
      grid: { show: false },
      legend: { show: false },
      tooltip: {
        y: {
          formatter: (val, opts) => {
            const actual = pyramidData.actualCounts?.[opts.dataPointIndex] ?? val;
            return `${actual} leads`;
          },
        },
      },
    }),
    [pyramidData]
  );

  const currentDate = new Date().toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const currentTime = new Date().toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
  });

  return (
    <>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              <div className="card">
                {/* <div className="card-header">
                 
                  <div className="row align-items-center">
                    <div className="col-sm-12">
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="page-header mb-0">
                          <div className="row align-items-center">
                            <h4 className="welcome-text mb-0 ">Dashboard</h4>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div> */}
                <div className="card-body">
                  <div className="user-dashboard">
                    {/* Onboarding / Getting Started Widget */}
                    <GettingStartedWidget />

                    {/* Date range picker */}
                    <div className="dash-tab-group-wrapper mb-3">
                      <div className="dash-tab-group">
                        {DATE_TABS.map((tab) => (
                          <button
                            key={tab.id}
                            className={`dash-tab-btn${activeTab === tab.id ? " dash-tab-btn--active" : ""}`}
                            onClick={() => applyTab(tab)}
                          >
                            {tab.label}
                          </button>
                        ))}
                        <button
                          className={`dash-tab-btn${activeTab === "custom" ? " dash-tab-btn--active" : ""}`}
                          onClick={() => { setActiveTab("custom"); setShowCustomPicker(true); }}
                        >
                          Custom
                        </button>
                        {showCustomPicker && (
                          <DatePicker.RangePicker
                            size="small"
                            disabledDate={(d) => d && d.isAfter(dayjs(), "day")}
                            onChange={handleCustomRange}
                            className="dash-custom-picker"
                            allowClear={false}
                          />
                        )}
                      </div>
                    </div>

                    {/* Quick Stats */}
                    <DashboardStats role="admin" startDate={startDate} endDate={endDate} />
                    <div className="ds-grid mb-4">
                      <div className="ds-card ds-card--inbound">
                        <div className="ds-top">
                          <span className="ds-label">Inbound Calls</span>
                          <div className="ds-icon"><IoCall size={18} /></div>
                        </div>
                        {loadingGraph ? <div className="ds-skeleton-val" /> : <div className="ds-value">{inboundTotal}</div>}
                      </div>
                      <div className="ds-card ds-card--outbound">
                        <div className="ds-top">
                          <span className="ds-label">Outbound Calls</span>
                          <div className="ds-icon"><IoCallOutline size={18} /></div>
                        </div>
                        {loadingGraph ? <div className="ds-skeleton-val" /> : <div className="ds-value">{outboundTotal}</div>}
                      </div>
                      <div className="ds-card ds-card--missed">
                        <div className="ds-top">
                          <span className="ds-label">Missed Calls</span>
                          <div className="ds-icon"><FaPhoneSlash size={16} /></div>
                        </div>
                        {loadingHistory ? <div className="ds-skeleton-val" /> : <div className="ds-value">{missed}</div>}
                      </div>
                      <div className="ds-card ds-card--calls-total">
                        <div className="ds-top">
                          <span className="ds-label">Total Calls</span>
                          <div className="ds-icon"><IoTrendingUp size={18} /></div>
                        </div>
                        {loadingGraph ? <div className="ds-skeleton-val" /> : <div className="ds-value">{graphTotal}</div>}
                      </div>
                    </div>
































                    {/* Charts Section — 3 per row on desktop, 1 per row on mobile */}
                    <Row gutter={[24, 24]} className="charts-section" style={{ alignItems: "stretch" }}>
                      {/* Daily Calls Over Time */}
                      <Col xs={24} lg={12} style={{ display: "flex", flexDirection: "column" }}>
                        <Card
                          title={`Calls from ${formatDisplayDate(startDate)} to ${formatDisplayDate(endDate)}`}
                          className="chart-card"
                          style={{ height: "100%" }}
                        >
                          {loadingGraph ? (
                            <div style={{ height: 350, display: "flex", justifyContent: "center", alignItems: "center" }}>
                              <Spin size="large" />
                            </div>
                          ) : !callDays.length ? (
                            <div style={{
                              height: 350, display: "flex", flexDirection: "column",
                              alignItems: "center", justifyContent: "center", gap: 12,
                            }}>
                              <i className="ti ti-phone-off" style={{ fontSize: 52, color: "#d1d5db" }} />
                              <div style={{ textAlign: "center" }}>
                                <p style={{ fontSize: 15, fontWeight: 600, color: "#9ca3af", margin: 0 }}>No call data</p>
                                <p style={{ fontSize: 13, color: "#d1d5db", margin: "4px 0 0" }}>No calls were recorded in this period</p>
                              </div>
                            </div>
                          ) : (
                            <ReactApexChart
                              key={`line-${startDate}-${endDate}`}
                              options={lineChartOptions}
                              series={lineChartSeries}
                              type="line"
                              height={350}
                            />
                          )}
                        </Card>
                      </Col>

                      {/* Call Distribution */}
                      <Col xs={24} lg={6} style={{ display: "flex", flexDirection: "column" }}>
                        <Card title="Call Success Rate" className="chart-card" style={{ height: "100%" }}>
                          {loadingHistory ? (
                            <div style={{ height: 350, display: "flex", justifyContent: "center", alignItems: "center" }}>
                              <Spin />
                            </div>
                          ) : (
                            <ReactApexChart
                              key={`radial-${startDate}-${endDate}-${totalCalls}`}
                              options={pieChartOptions}
                              series={finalSeries}
                              type="radialBar"
                              height={350}
                            />
                          )}
                        </Card>
                      </Col>

                      {/* Lead Status Pyramid */}
                      <Col xs={24} lg={6} style={{ display: "flex", flexDirection: "column" }}>
                        <Card title="Lead Status Pyramid" className="chart-card" style={{ height: "100%" }}>
                          {loadingLeadCounts ? (
                            <div style={{ height: 350, display: "flex", justifyContent: "center", alignItems: "center" }}>
                              <Spin />
                            </div>
                          ) : pyramidData.series.length > 0 ? (
                            <ReactApexChart
                              key={`pyramid-${pyramidData.series.join("-")}`}
                              options={funnelChartOptions}
                              series={[{ name: "Leads", data: pyramidData.series }]}
                              type="bar"
                              height={350}
                            />
                          ) : (
                            <div style={{ height: 350, display: "flex", alignItems: "center", justifyContent: "center" }}>
                              <Text type="secondary">No lead status data available</Text>
                            </div>
                          )}
                        </Card>
                      </Col>
                    </Row>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Dashboard;
